import {type FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {match, Pattern} from 'ts-pattern';

import {isPlainObj} from 'ramda-adjunct';

import {ApiError, isFetchBaseQueryError} from 'shared/web';

import {getNestedApiErrorMessages} from './getApiErrorMessages';

export type ApiErrorOrigin = 'business' | 'defect';
export type ApiErrorClassification = {origin: ApiErrorOrigin; isAmbiguous: boolean};

// The backend wraps these infrastructure failures as coded 400s
// (UnexpectedExceptionForGeneratedInvoicingDocumentNumber, GoodDataRequestFailed,
// LlmUnavailableException). Remove entries when the backend re-maps them to 5xx.
const KNOWN_DEFECT_ERROR_CODES = ['DMS-AC-SG-0003', 'DMS-GD-GD-0001', 'DMS-SV-AD-0053'] as const;

const NO_CODE = '???';
const HTTP_STATUS_CLIENT_ERROR_MIN = 400;
const HTTP_STATUS_SERVER_ERROR_MIN = 500;
const HTTP_STATUS_UNAUTHORIZED = 401;

const DEFECT_CLASSIFICATION: ApiErrorClassification = {origin: 'defect', isAmbiguous: false};
const BUSINESS_CLASSIFICATION: ApiErrorClassification = {
  origin: 'business',
  isAmbiguous: false,
};
const AMBIGUOUS_DEFECT_CLASSIFICATION: ApiErrorClassification = {
  origin: 'defect',
  isAmbiguous: true,
};

type ApiErrorData = Record<string, unknown>;

const isObject = (value: unknown): value is Record<string, unknown> => isPlainObj(value);

const isApiError = (error: unknown): error is ApiError => isPlainObj(error);

const isFetchBaseQueryErrorValue = (error: unknown): error is FetchBaseQueryError =>
  isApiError(error) && isFetchBaseQueryError(error);

export const getNumericApiErrorStatus = (error: unknown) =>
  isFetchBaseQueryErrorValue(error) && typeof error.status === 'number' ? error.status : undefined;

const isClientError = (error: FetchBaseQueryError) => {
  const status = getNumericApiErrorStatus(error);

  return (
    typeof status === 'number' &&
    status >= HTTP_STATUS_CLIENT_ERROR_MIN &&
    status < HTTP_STATUS_SERVER_ERROR_MIN
  );
};

const getErrorData = (error: FetchBaseQueryError): ApiErrorData | undefined =>
  isObject(error.data) ? error.data : undefined;

const getErrors = (error: FetchBaseQueryError): unknown[] => {
  const errors = getErrorData(error)?.errors;

  return Array.isArray(errors) ? errors : [];
};

const hasKnownDefectCode = (error: FetchBaseQueryError) =>
  getErrors(error).some(
    (item) =>
      isObject(item) &&
      typeof item.code === 'string' &&
      KNOWN_DEFECT_ERROR_CODES.some((code) => code === item.code)
  );

const hasValidationErrors = (error: FetchBaseQueryError) => {
  const validationErrors = getErrorData(error)?.validationErrors;

  return Array.isArray(validationErrors) && validationErrors.length > 0;
};

const hasBusinessErrorCode = (error: FetchBaseQueryError) =>
  getErrors(error).some(
    (item) =>
      isObject(item) &&
      typeof item.code === 'string' &&
      item.code.length > 0 &&
      item.code !== NO_CODE &&
      typeof item.message === 'string' &&
      item.message.length > 0
  );

const hasNestedValidationDetails = (error: FetchBaseQueryError) =>
  getErrors(error).some(
    (item) => isObject(item) && getNestedApiErrorMessages(item.message).length > 0
  );

const getFetchBaseQueryErrorOrigin = (error: FetchBaseQueryError): ApiErrorClassification =>
  match(error)
    .with({status: Pattern.number.gte(HTTP_STATUS_SERVER_ERROR_MIN)}, () => DEFECT_CLASSIFICATION)
    .with({status: HTTP_STATUS_UNAUTHORIZED}, () => BUSINESS_CLASSIFICATION)
    .when(
      (value) => isClientError(value) && hasKnownDefectCode(value),
      () => DEFECT_CLASSIFICATION
    )
    .when(
      (value) => isClientError(value) && hasValidationErrors(value),
      () => BUSINESS_CLASSIFICATION
    )
    .when(
      (value) => isClientError(value) && hasBusinessErrorCode(value),
      () => BUSINESS_CLASSIFICATION
    )
    .when(
      (value) => isClientError(value) && hasNestedValidationDetails(value),
      () => BUSINESS_CLASSIFICATION
    )
    .when(isClientError, () => AMBIGUOUS_DEFECT_CLASSIFICATION)
    .otherwise(() => DEFECT_CLASSIFICATION);

export const getApiErrorOrigin = (error: unknown): ApiErrorClassification =>
  match(error)
    .when(isFetchBaseQueryErrorValue, getFetchBaseQueryErrorOrigin)
    .otherwise(() => DEFECT_CLASSIFICATION);
